import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { WfDomainMoveDialogComponent } from './domain-move-dialog.component';

const DESTINATION_HOST = 'gotlivedata.com';
const SNOOZE_STORAGE_KEY = 'wfDomainMoveBannerSnoozedAt';
const SNOOZE_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Inline top bar announcing the move to gotlivedata.com. Mounted at the top of
 * the Workforce shell's content area (the rail keeps its full height) and on the
 * full-page workforce login/signup, pushing content down instead of overlaying
 * it; hidden once the user is already on the destination domain. "Remind me
 * later" snoozes it for a day.
 */
@Component({
  selector: 'app-wf-domain-move-banner',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './domain-move-banner.component.html',
})
export class WfDomainMoveBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly HEIGHT_VAR = '--wf-move-banner-height';
  private static readonly BODY_CLASS = 'wf-move-banner-visible';

  // Sticky so the bar stays visible when the surrounding page scrolls; it still
  // occupies normal flow, so content below is pushed down rather than overlaid.
  @HostBinding('class') hostClass = 'tw-block tw-sticky tw-top-0 tw-z-10';

  visible = false;

  private resizeObserver: ResizeObserver | undefined;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private dialog: MatDialog
  ) {}

  // The same routes exist on the destination domain (the Workforce experience is
  // the default everywhere), so carry the current location across. A getter keeps
  // it fresh as the user navigates within the SPA.
  get targetUrl(): string {
    const { pathname, search, hash } = window.location;
    return `https://${DESTINATION_HOST}${pathname}${search}${hash}`;
  }

  ngOnInit(): void {
    this.visible = !this.isOnDestinationHost() && !this.isSnoozed();
    if (this.visible) {
      this.startPublishingHeight();
    }
  }

  ngAfterViewInit(): void {
    // The observer's first delivery can land after the first paint; publish the
    // rendered height synchronously once the view exists so full-viewport pages
    // never see a frame sized with the 0px fallback.
    if (this.visible) {
      this.publishHeight();
    }
  }

  ngOnDestroy(): void {
    this.stopPublishingHeight();
  }

  // "Take me there" opens a short explainer first — the new domain asks for a
  // one-time re-login with the user's existing credentials, and springing that
  // on them without warning would read as being logged out.
  takeMeThere(): void {
    this.dialog.open(WfDomainMoveDialogComponent, {
      width: '460px',
      data: { targetUrl: this.targetUrl },
    });
  }

  remindLater(): void {
    localStorage.setItem(SNOOZE_STORAGE_KEY, String(Date.now()));
    this.visible = false;
    this.stopPublishingHeight();
  }

  private isOnDestinationHost(): boolean {
    const hostname = window.location.hostname;
    return hostname === DESTINATION_HOST || hostname.endsWith(`.${DESTINATION_HOST}`);
  }

  private isSnoozed(): boolean {
    const snoozedAt = Number(localStorage.getItem(SNOOZE_STORAGE_KEY));
    return snoozedAt > 0 && Date.now() - snoozedAt < SNOOZE_DURATION_MS;
  }

  // Full-viewport pages (tw-h-screen, .container) size themselves against 100vh,
  // so while the banner is visible its measured height is published as a CSS
  // variable and a body class scopes the compensating rules in styles.scss —
  // pages shrink by the banner height instead of overflowing below the fold.
  private startPublishingHeight(): void {
    document.body.classList.add(WfDomainMoveBannerComponent.BODY_CLASS);
    this.resizeObserver = new ResizeObserver(() => this.publishHeight());
    this.resizeObserver.observe(this.hostRef.nativeElement);
  }

  private publishHeight(): void {
    document.documentElement.style.setProperty(
      WfDomainMoveBannerComponent.HEIGHT_VAR,
      `${this.hostRef.nativeElement.offsetHeight}px`
    );
  }

  private stopPublishingHeight(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    document.body.classList.remove(WfDomainMoveBannerComponent.BODY_CLASS);
    document.documentElement.style.removeProperty(WfDomainMoveBannerComponent.HEIGHT_VAR);
  }
}
